import {
    SELECTED_THEME_KEY,
    ThemeOption,
} from '@rhg/components/theme-selector';
import commonMessages from '@rhg/lang/messages/common';
import { useTheme } from '@rhg/libs/material';
import makeTheme, { ColorMode } from '@rhg/libs/material/theme';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import usePrefersDarkMode from '../use-prefers-dark-mode';

const getMode = (
    forceMode: ColorMode | undefined,
    storedTheme: ThemeOption,
    prefersDarkMode: boolean,
    isThemeSelectorEnabled: boolean
) => {
    if (forceMode) {
        return forceMode;
    }

    if (isThemeSelectorEnabled && storedTheme !== 'unset') {
        return storedTheme;
    }

    return prefersDarkMode ? 'dark' : 'light';
};

/**
 * Provides the app with a theme depending on page and dark mode preference
 * When trying to access a theme please instead use the `useTheme()` hook from '@rhg/libs/material'
 */
export function useProvidedTheme(
    forceMode: ColorMode | undefined = undefined,
    isThemeSelectorEnabled = false,
    themeQueryParam?: string
) {
    const [storedTheme, setStoredTheme] = useState<ThemeOption>('unset');

    useEffect(() => {
        // initialize on load
        // using an effect to ensure this runs only on the client without getting a client-server mismatch

        // eslint-disable-next-line no-restricted-syntax
        const storedValue = localStorage.getItem(SELECTED_THEME_KEY);
        if (!storedValue) {
            setStoredTheme('unset');
        }
        setStoredTheme(storedValue as ThemeOption);
    }, [setStoredTheme]);

    useEffect(() => {
        // update when a different option is selected, triggered by setting a query parameter
        if (themeQueryParam) {
            setStoredTheme(themeQueryParam as ThemeOption);
        }
    }, [themeQueryParam]);

    const prefersDarkMode = usePrefersDarkMode();

    const { formatMessage } = useIntl();

    const mode = getMode(
        forceMode,
        storedTheme,
        prefersDarkMode,
        isThemeSelectorEnabled
    );

    return makeTheme(
        `"(${formatMessage(commonMessages['common.required'])})"`,
        mode
    );
}

export function useIsDarkMode() {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    return isDarkMode;
}
